import React, { JSX } from "react";

import { AgeBandedRate, BasicLifeRate } from "../../constants/aquafina";
import {
  CoverageArrowRow,
  CoverageDividerRow,
  CoverageHeaderRow,
  CoverageRow,
} from "../table/rows";
import { CoverageCell, CoverageHeaderCell } from "../table/cells";
import { RangeIterator } from "../../utils";

import AgeBandedRates from "./age-banded-rates";

const BasicLifeRates = ({ rates }: { rates: BasicLifeRate[] }): JSX.Element => {
  const isAgeBanded = rates.some((item) => !!item.ageBandedRate);
  let ageBandedTiers: AgeBandedRate[] | undefined;
  let eeBasicLifeRateRowCount = 2;
  if (isAgeBanded) {
    ageBandedTiers = rates.map((item) => item.ageBandedRate);
    eeBasicLifeRateRowCount += calculateAgeBandsRowCount(ageBandedTiers);
  }

  const hasSpouseRate = rates.some((r) => !!r.spouseRate);
  const hasChildRate = rates.some((r) => !!r.childRate);
  const hasDependentRate = rates.some((r) => !!r.dependentRate);

  // When both spouse and child rates exist, check if they are equal across all tiers.
  // If equal, display as a single "Dependent Rate" row; if different, show nested rows.
  const ratesAreEqual =
    hasSpouseRate &&
    hasChildRate &&
    rates.every((r) => r.spouseRate === r.childRate);

  // Show a single dependent rate row when:
  // - both spouse & child rates exist and are equal, OR
  // - only one of spouse/child exists, OR
  // - neither spouse nor child rate exists but dependentRate is populated (fallback)
  const showSingleDependentRate =
    ratesAreEqual ||
    hasSpouseRate !== hasChildRate ||
    (!hasSpouseRate && !hasChildRate && hasDependentRate);

  // Show nested spouse/child rows when both exist and differ
  const showNestedRates = hasSpouseRate && hasChildRate && !ratesAreEqual;

  // Resolve the single dependent rate value per tier, falling back to dependentRate
  const getDependentRateValue = (rate: BasicLifeRate): string | null => {
    if (rate.spouseRate) return rate.spouseRate;
    if (rate.childRate) return rate.childRate;
    if (rate.dependentRate) return rate.dependentRate;
    return null;
  };

  // Resolve a tier-specific rate (spouse or child), falling back to dependentRate
  const getTierRateValue = (
    rate: BasicLifeRate,
    tierRate: string | undefined,
  ): string | null => tierRate || rate.dependentRate || null;

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(rates.length, (index) => (
          <CoverageHeaderCell label="Employee" key={index} />
        ))}
      </CoverageHeaderRow>
      {isAgeBanded ? (
        <AgeBandedRates rates={ageBandedTiers!} headerTitle="Basic Life Rate" />
      ) : (
        <>
          <CoverageRow label="Rate Unit">
            {RangeIterator(rates.length, (index) => (
              <CoverageCell value={rates[index].rateUnit} key={index} />
            ))}
          </CoverageRow>
          <CoverageRow label="Basic Life Rate" isWhite>
            {RangeIterator(rates.length, (index) => (
              <CoverageCell value={rates[index].basicLifeRate} key={index} />
            ))}
          </CoverageRow>
        </>
      )}
      <CoverageRow
        label="AD&D Rate"
        isWhite={eeBasicLifeRateRowCount % 2 !== 0}
      >
        {RangeIterator(rates.length, (index) => (
          <CoverageCell value={rates[index].adndRate} key={index} />
        ))}
      </CoverageRow>
      {showSingleDependentRate && (
        <CoverageRow
          label="Dependent Rate"
          isWhite={eeBasicLifeRateRowCount % 2 === 0}
        >
          {RangeIterator(rates.length, (index) => (
            <CoverageCell
              value={getDependentRateValue(rates[index])}
              key={index}
            />
          ))}
        </CoverageRow>
      )}
      {showNestedRates && (
        <>
          <CoverageDividerRow
            label="Dependent Rate"
            totalPlans={rates.length}
            isWhite={eeBasicLifeRateRowCount % 2 === 0}
          />
          <CoverageArrowRow label="Spouse">
            {RangeIterator(rates.length, (index) => (
              <CoverageCell
                value={getTierRateValue(rates[index], rates[index].spouseRate)}
                key={index}
              />
            ))}
          </CoverageArrowRow>
          <CoverageArrowRow label="Child" isWhite={true}>
            {RangeIterator(rates.length, (index) => (
              <CoverageCell
                value={getTierRateValue(rates[index], rates[index].childRate)}
                key={index}
              />
            ))}
          </CoverageArrowRow>
        </>
      )}
    </>
  );
};

const calculateAgeBandsRowCount = (ageBandedRates: AgeBandedRate[]): number =>
  new Set(ageBandedRates.flatMap((item) => item.tiers.map((tier) => tier.name)))
    .size;

export default BasicLifeRates;
